import React, { useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import CandidatSelectionsList from './Modals/CandidatSelection/CandidatSelectionsList';
import { deleteCandidatFromSelection, getAllCandidatSelections } from '../../../../services/SelectionCandidatService';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { updateObjectStateValue } from '../../../../utils/FunctionHelpers';
import DialogDeleteCustom from '../../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Sélections');

const Selections = ({ snackbarShowMessage }) => {
  const { idCandidat } = useParams();
  const [selection, setSelection] = useState({});
  const [selectionsData, setSelectionsData] = useState({ isLoading: false, data: [] });
  const [reloadingData, setReloadingData] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);
  const mounted = React.useRef(true);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);

  const loadSelections = () => {
    updateObjectStateValue(setSelectionsData, { isLoading: true, data: [] });
    getAllCandidatSelections(idCandidat)
      .then(({ data }) => updateObjectStateValue(setSelectionsData, { isLoading: false, data: data.selectionsCandidat }))
      .catch(() => snackbarShowMessage('Erreur de chargement des sélections.', 'error', 4000))
      .finally(() => updateObjectStateValue(setSelectionsData, { isLoading: false }));
  };

  React.useEffect(() => { loadSelections(); }, [reloadingData]);

  const handleOpenDialog = (row) => { setSelection(row); setOpenDialogDelete(true); };
  const handleCloseDialog = () => { if (!loadingToDelete) setOpenDialogDelete(false); };

  const deleteCandidatFromList = () => {
    (async () => {
      setLoadingToDelete(true);
      try {
        const res = await deleteCandidatFromSelection(idCandidat, selection.id);
        snackbarShowMessage(res.data);
        if (!mounted.current) return;
        setOpenDialogDelete(false);
        setReloadingData(!reloadingData);
      } catch {
        snackbarShowMessage('Erreur de suppression de la données, veuillez réssayer plutard.', 'error', 4000);
      } finally {
        setLoadingToDelete(false);
      }
    })();
  };

  const columns = [
    {
      field: 'dateAjout', headerName: "DATE D'AJOUT", width: 130,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'nom', headerName: 'NOM SÉLECTION', flex: 1, minWidth: 180,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'auteur', headerName: 'AUTEUR', width: 160,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
            sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = (selectionsData.data || []).map((s) => ({ ...s }));

  return (
    <>
      <Box>
        <CandidatSelectionsList loadSelectionsData={loadSelections} snackbarShowMessage={snackbarShowMessage} selectionsCandidat={selectionsData.data} />
        <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '44vh', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)', mt: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={selectionsData.isLoading}
            pageSizeOptions={[50, 100]}
            disableRowSelectionOnClick
            showToolbar
            slots={{ toolbar: CustomToolbar }}
            slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
            localeText={gridLocaleText}
            sx={commonGridSx}
          />
        </Box>
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog} open={openDialogDelete} handleConfirm={deleteCandidatFromList}
        loading={loadingToDelete}
        label={`Voulez vous vraiment supprimer le candidat de la selection ${selection.nom}?`}
        title="Supprimer le candidat de la sélection"
      />
    </>
  );
};

export default withSnackbar(Selections);

